// Reports (handoff §7l): the shapes GET /api/reports and /api/reports/:key
// return, and the display rules for them. The server builds every figure;
// this file only formats, so a report and its export always agree.
import { dmy, money } from '../wages/format'
import type { CellValue } from '../batches/xlsx'
import { isoLocal } from '../timesheets/time'

export type Fmt = 'text' | 'int' | 'lugs' | 'kg' | 'hours' | 'money' | 'money_h' | 'rate' | 'pct' | 'date'

export interface ReportCategory {
  key: string
  label: string
  dark?: boolean
}

export interface ReportDef {
  key: string
  cat: string
  name: string
  blurb: string
  dates: 'range' | 'season' | 'none'
  filters: FilterKey[]
  exports: string[]
  live?: boolean
  map?: boolean
  built: boolean
  // A live dashboard's period buttons, in order (§7l.7).
  periods?: { k: string; label: string }[]
}

// ─── live dashboards (§7l.7) ────────────────────────────────────────────────

interface LiveBase {
  key: string
  name: string
  period: string
  window: { from: string | null; to: string }
  season_start: string | null
  notes: string[]
}
// Realtime Collection: today's Self Scans still waiting in the field for a
// receipt, aged on the server's clock (report-build.js buildRealtime).
export type AgeBucket = 'fresh' | 'warm' | 'late' | 'critical'
export interface RealtimeItem {
  cycle_id: string
  item_code: string | null
  // A shared bin's shares are one item: the bin's printed ID, and how many.
  big_bin: string | null
  shares: number
  workers: string[]
  team: string | null
  site: string | null
  location: string | null
  work_date: string
  picked_at: string
  minutes: number
  bucket: AgeBucket
  gps: { lat: number; lng: number } | null
}
// The day for the map's playback (asked with history=1): every Self Scan item
// picked today, waiting or received, with its pick and first receipt.
export interface RealtimeDayItem {
  cycle_id: string
  item_code: string | null
  big_bin: string | null
  shares: number
  workers: string[]
  location: string | null
  picked_at: string
  received_at: string | null
  gps: { lat: number; lng: number } | null
}
export interface RealtimeResult extends LiveBase {
  as_at: string
  total: number
  off_map: number
  buckets: Record<AgeBucket, number>
  items: RealtimeItem[]
  day?: RealtimeDayItem[]
}
export type RealtimeView = Pick<RealtimeResult, 'total' | 'off_map' | 'buckets' | 'items'>

// The server's bubbles (report-build.js AGE_BUCKETS): up to 20, 40 and 60 minutes, then over.
export const ageBucketOf = (minutes: number): AgeBucket => (minutes <= 20 ? 'fresh' : minutes <= 40 ? 'warm' : minutes <= 60 ? 'late' : 'critical')

// What was waiting at a moment of the day: picked by then and not yet received,
// aged at that moment, oldest first, as the server lists the live items.
export function realtimeAt(day: RealtimeDayItem[], atMs: number): RealtimeView {
  const items: RealtimeItem[] = day
    .filter((d) => Date.parse(d.picked_at) <= atMs && (!d.received_at || Date.parse(d.received_at) > atMs))
    .map((d) => {
      const minutes = Math.max(0, Math.floor((atMs - Date.parse(d.picked_at)) / 60000))
      return { ...d, team: null, site: null, work_date: '', minutes, bucket: ageBucketOf(minutes) }
    })
    .sort((a, b) => b.minutes - a.minutes)
  const buckets: Record<AgeBucket, number> = { fresh: 0, warm: 0, late: 0, critical: 0 }
  for (const it of items) buckets[it.bucket]++
  return { total: items.length, off_map: items.filter((it) => !it.gps).length, buckets, items }
}

// Playback runs a 6-hour day in 20 seconds.
export const PLAYBACK_RATE = (6 * 3600) / 20
// The timeline: from the hour of the day's first pick (an hour back when none) to now.
export function playbackRange(day: RealtimeDayItem[] | undefined, asAt: string) {
  const end = Date.parse(asAt)
  const first = day?.length ? Math.min(...day.map((d) => Date.parse(d.picked_at))) : end - 3600000
  const start = new Date(Math.min(first, end))
  start.setMinutes(0, 0, 0)
  return { start: start.getTime(), end }
}
// Crew's four bubbles (receipt_screen.dart), in its colours: sage, then the
// terracotta ramp. Only the last warns, and only when something is in it.
export const AGE_BUCKETS: { k: AgeBucket; label: string; color: string; warn?: boolean }[] = [
  { k: 'fresh', label: '0-20m', color: '#7f9c6b' },
  { k: 'warm', label: '21-40m', color: '#e0a15a' },
  { k: 'late', label: '41-60m', color: '#c8673a' },
  { k: 'critical', label: '61m+', color: '#8f2f1c', warn: true },
]
export const bucketColor = (k: AgeBucket) => AGE_BUCKETS.find((b) => b.k === k)?.color || '#c8673a'
export const waitingLabel = (minutes: number) => (minutes >= 60 ? `${Math.floor(minutes / 60)}h ${minutes % 60}m` : `${minutes}m`)
export const realtimeItemName = (it: RealtimeItem, chip: (c: string | null) => string) =>
  it.big_bin ? `Bin ${it.big_bin} (shared)` : chip(it.item_code) || 'Item'

export interface LeaderRow {
  rank: number
  human_id: string
  name: string
  teams: string[]
  rate: number
  items: number
  kg: number
  hours: number
  gated: boolean
}
export interface LeaderResult extends LiveBase {
  team: string | null
  teams: { id: string; name: string }[]
  gate_hours: number
  rows: LeaderRow[]
}
export interface Strike {
  cycle_id: string
  item_code: string
  work_date: string
  quality: string
  location: string | null
  gross_kg: number | null
  credited_kg: number | null
  factor: number | null
  provisional: boolean
  // The factor is a tied QC scan's, not yet in the credited weight (28g).
  pending?: boolean
  photo: string | null
}
export interface StrikeWorker {
  human_id: string
  name: string
  n: number
  issues: string[]
  strikes: Strike[]
}
export interface StrikeResult extends LiveBase {
  total: number
  workers: StrikeWorker[]
}

export const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()

// V4 tally: gates of four uprights and a diagonal, the remainder as uprights.
export function tallyGroups(n: number): { five: boolean; marks: number }[] {
  const out: { five: boolean; marks: number }[] = []
  for (let k = 0; k < Math.floor(n / 5); k++) out.push({ five: true, marks: 4 })
  if (n % 5) out.push({ five: false, marks: n % 5 })
  return out
}

export const strikeTotal = (r: StrikeResult) =>
  `${r.total} ${r.total === 1 ? 'strike' : 'strikes'} · ${r.workers.length} ${r.workers.length === 1 ? 'worker' : 'workers'}`

export const strikeEmpty = (period: string) =>
  period === 'today' ? 'No strikes today. Nothing has failed a quality check.' : 'No strikes this week.'

// §7l.3: Dataphyll's date when one is set (Client Setup), else the first
// picking day.
export type SeasonSource = 'dataphyll' | 'inferred'

export interface Library {
  categories: ReportCategory[]
  reports: ReportDef[]
  season_start: string | null
  season_source?: SeasonSource
}

export type Drill =
  | { to: 'product'; params: Record<string, string> }
  | { to: 'batches'; params: Record<string, string> }
  | { to: 'timesheets'; params: Record<string, string> }

export interface ReportResult {
  key: string
  name: string
  cat: string
  scope: { mode: 'range' | 'season'; from: string | null; to: string | null; season_start: string | null; season_source?: SeasonSource }
  kpis: { label: string; value: CellValue; fmt: Fmt; note?: string }[]
  chart: { label: string; v: number; date?: string }[] | null
  chart_unit: string
  cols: { label: string; fmt: Fmt }[]
  rows: { cells: CellValue[]; drill?: Drill | null }[]
  drill: string
  map: boolean
  notes: string[]
  options: { products: string[]; jobs: string[]; teams: { id: string; name: string }[]; contractors?: string[] }
}

export type FilterKey = 'product' | 'job' | 'team' | 'contractor'
export type Filters = Record<FilterKey, string>
export const NO_FILTERS: Filters = { product: '', job: '', team: '', contractor: '' }

// What a filter's "everything" choice and its control are called.
export const FILTER_WORDS: Record<FilterKey, { all: string; label: string }> = {
  product: { all: 'All products', label: 'Product' },
  job: { all: 'All jobs', label: 'Picking job' },
  team: { all: 'All teams', label: 'Team' },
  contractor: { all: 'All contractors', label: 'Contractor' },
}

// A saved view (§7l.5; GET /api/report-views): a report and its
// configuration under a name, shared by the tenant. 'period' opens on the
// viewer's open period, never on fixed dates.
export interface SavedView {
  id: string
  report_key: string
  name: string
  date_mode: 'period' | 'season'
  filters: Partial<Filters>
  team_name: string | null
  version: number
  created_at: string
  created_by: string
  updated_at: string
  updated_by: string
}

// A stored timestamp's day on this wall calendar, not UTC's: slicing the ISO
// string would print yesterday for a view saved on a New Zealand morning.
const isoDay = (ts: string) => {
  const d = new Date(ts)
  return Number.isNaN(d.getTime()) ? (ts || '').slice(0, 10) : isoLocal(d)
}

// "Daily Harvest Summary · saved by Jane · 28 Aug 2026", and who last changed
// it when that is someone or something since.
export function viewMeta(v: SavedView, reportName: string): string {
  let s = `${reportName} · saved by ${v.created_by} · ${dmy(isoDay(v.created_at))}`
  if (v.version > 1) s += ` · edited by ${v.updated_by} · ${dmy(isoDay(v.updated_at))}`
  return s
}

// What a view would store, in words: the report, its dates and each filter.
export function viewConfig(def: ReportDef, scope: string, f: Filters, teamName: string | null): string {
  const words = def.filters.map((k) => (k === 'team' ? (f.team ? teamName || 'Chosen team' : 'All teams') : f[k] || FILTER_WORDS[k].all))
  return [def.name, scope, ...words].join(' · ')
}

export function viewFilters(def: ReportDef, f: Filters): Partial<Filters> {
  const out: Partial<Filters> = {}
  for (const k of def.filters) if (f[k]) out[k] = f[k]
  return out
}

// The category tones, the design's own (V4 RP_CATS): ink, tone, soft, line.
export const CAT_TONES: Record<string, { ink: string; tone: string; soft: string; line: string }> = {
  prod: { ink: '#3f4a2c', tone: '#7a8a5e', soft: '#eef2e2', line: '#d3ddba' },
  work: { ink: '#8c491a', tone: '#c67139', soft: '#fdf1e8', line: '#f0cdb2' },
  trace: { ink: '#6e3312', tone: '#a2512a', soft: '#f8e6da', line: '#e9c6ae' },
  live: { ink: '#f3ebdc', tone: '#e8b98c', soft: '#221f1a', line: '#3a352d' },
}

// One glyph per report, drawn at 24×24 on currentColor (V4 RP_ICONS).
export const REPORT_ICONS: Record<string, string> = {
  daily: 'M3 17h4V9H3ZM10 17h4V4h-4ZM17 17h4v-6h-4Z',
  season: 'M3 16.5 8 11l4 3.5L21 5M21 5h-5M21 5v5',
  quality: 'M12 3 4 6v6c0 4.5 3.4 7.9 8 9 4.6-1.1 8-4.5 8-9V6ZM9 12l2 2 4-4',
  yield: 'M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0ZM12 12h.01',
  locrow: 'M4 4h16v4H4ZM4 10h16v4H4ZM4 16h16v4H4Z',
  variety: 'M12 3a9 9 0 1 0 9 9h-9Z M12 3v9h9A9 9 0 0 0 12 3Z',
  worker: 'M16 20v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2M9.5 6.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0M18 8v6M21 11h-6',
  task: 'M12 7v5l3 2M12 3a9 9 0 1 0 9 9 9 9 0 0 0-9-9Z',
  perf: 'M4 20V10M10 20V4M16 20v-7M22 20H2',
  contractor: 'M3 21h18M5 21V8l7-5 7 5v13M9 21v-6h6v6M9 11h.01M15 11h.01',
  batch: 'M3 8 12 3l9 5-9 5ZM3 8v8l9 5 9-5V8M12 13v8',
  realtime: 'M12 12h.01M8.5 8.5a5 5 0 0 0 0 7M15.5 15.5a5 5 0 0 0 0-7M5.5 5.5a9 9 0 0 0 0 13M18.5 18.5a9 9 0 0 0 0-13',
  leader: 'M8 21h8M12 17v4M7 4h10v5a5 5 0 0 1-10 0ZM17 5h3v2a3 3 0 0 1-3 3M7 5H4v2a3 3 0 0 0 3 3',
  strike: 'M12 3v18M3 12h18M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18ZM12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8Z',
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/

function group(n: number, dp: number): string {
  let s = (Math.round(n * 10 ** dp) / 10 ** dp).toFixed(dp)
  if (s.includes('.')) s = s.replace(/0+$/, '').replace(/\.$/, '')
  const [i, f] = s.split('.')
  const g = i.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return f ? g + '.' + f : g
}

// A cell as the screen prints it. null is "nothing to show" (—); a string in
// a numeric column is the server's own words ("No picking") and prints as is.
export function cellText(v: CellValue, fmt: Fmt): string {
  if (v === null || v === undefined || v === '') return '—'
  if (typeof v === 'string') return fmt === 'date' && DATE_RE.test(v) ? dmy(v) : v
  switch (fmt) {
    case 'int':
      return group(v, 0)
    // A count QC can take part of: 4, or 3.75 when a lug was deducted 25%.
    case 'lugs':
      return group(v, Number.isInteger(v) ? 0 : 2)
    case 'kg':
      return group(v, 1) + ' kg'
    case 'hours':
      return group(v, 2) + ' h'
    case 'money':
      return money(v)
    case 'money_h':
      return money(v) + '/h'
    case 'rate':
      return group(v, 1) + ' kg/h'
    case 'pct':
      return group(v, 0) + '%'
    default:
      return String(v)
  }
}

export const isNumeric = (fmt: Fmt) => fmt !== 'text' && fmt !== 'date'

// The report table's columns, and the width every row is held to. Each row is its own grid, so without one width for
// all of them a row with longer text lays its columns out wider than the header's; a wide report (the Contractor
// Report's lug and rate pair per job) scrolls sideways instead of squeezing.
export function tableLayout(cols: { fmt: Fmt }[]): { gridTemplateColumns: string; minWidth: string } {
  const mins = cols.map((c, i) => (i === 0 ? 9.375 : c.fmt === 'text' ? 6.875 : 5))
  const gridTemplateColumns = cols.map((c, i) => `minmax(${mins[i]}rem,${i === 0 ? 2 : c.fmt === 'text' ? 1.4 : 1}fr)`).join(' ')
  // The columns, 0.75rem gaps between them and 1rem padding either side (.headRow, .row).
  const minWidth = `${mins.reduce((t, m) => t + m, 0) + Math.max(0, cols.length - 1) * 0.75 + 2}rem`
  return { gridTemplateColumns, minWidth }
}

export function noteText(note: string | undefined): string {
  if (!note) return ''
  return DATE_RE.test(note) ? dmy(note) : note
}

export function scopeLabel(scope: { mode: 'range' | 'season'; from: string | null; to: string | null; season_start: string | null }): string {
  if (scope.mode === 'season') return scope.season_start ? 'Season · from ' + dmy(scope.season_start) : 'Season · not started'
  if (!scope.from || !scope.to) return ''
  return scope.from === scope.to ? dmy(scope.from) : dmy(scope.from) + ' – ' + dmy(scope.to)
}

// Where a row's drill-through goes (§7l.4). Product Activity takes the
// report's own window unless the row names a day.
export function drillHref(d: Drill, scope: { from: string | null; to: string | null }): string {
  const p = new URLSearchParams()
  if (d.to === 'product') {
    const from = d.params.from || scope.from
    const to = d.params.to || d.params.from || scope.to
    if (from) p.set('from', from)
    if (to) p.set('to', to)
  }
  for (const [k, v] of Object.entries(d.params)) if (v && !p.has(k)) p.set(k, v)
  const path = d.to === 'product' ? '/product-activity' : d.to === 'batches' ? '/batches' : '/timesheets'
  return `${path}?${p}`
}

// The export sheet: the header row, then the rows as the server sent them, so
// a number stays a number in the spreadsheet.
export function exportRows(r: ReportResult): CellValue[][] {
  return [r.cols.map((c) => c.label), ...r.rows.map((row) => row.cells.map((v) => (v === null ? '' : v)))]
}

